import React, { useState, useEffect } from 'react';
import { Button, Checkbox, Col, DatePicker, Divider, Form, Input, InputNumber, Row, Select } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

// Components
import TagInput from '../../Elements/TagInput';

// Deps
import { DAY_FORMAT, ISSUE_TYPES, ORDER_STATUSES_DISPLAY, ORDER_TYPES_DISPLAY } from '../../../constants';
import { getPartnerItemTypes } from '../../../modules/partners/api';
import './OrderInfoForm.scss';

const OrderInfoForm = ({ order, types, setTypes, partners = [] }) => {
  const { t } = useTranslation('orders');
  const form = Form.useFormInstance();
  const selectedPartnerId = Form.useWatch('partnerId', form);
  const productDetailsKnown = Form.useWatch('productDetailsKnown', form);

  const [productTypes, setProductTypes] = useState([]);

  // Fetch PRODUCT item types when partner changes
  useEffect(() => {
    if (!selectedPartnerId) {
      setProductTypes([]);
      return;
    }
    getPartnerItemTypes(selectedPartnerId).then(({ response }) => {
      const items = Array.isArray(response) ? response : [];
      setProductTypes(items.filter((t) => t.category === 'PRODUCT' && t.isActive));
    });
  }, [selectedPartnerId]);

  return (
    <div className={`OrderInfoForm`}>
      <Row gutter={24}>
        <Col span={6}>
          <Form.Item name="name" label={t('form.name')} rules={[{ required: true, message: t('common:required') }]}>
            <Input placeholder={t('form.name')} />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item label={t('form.orderRef')} name="orderRef">
            <Input placeholder={t('form.orderRef')} />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item
            name="deliveryDate"
            label={t('form.deliveryDate')}
            rules={[{ required: true, message: t('common:required') }]}
          >
            <DatePicker format={DAY_FORMAT} disabled />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item name="orderType" label={t('form.orderType')}>
            <Select
              disabled
              options={Object.keys(ORDER_TYPES_DISPLAY).map((key) => ({ value: key, label: ORDER_TYPES_DISPLAY[key] }))}
            />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item name="volume" label={t('form.volume')}>
            <Input min={0} step={0.001} type="number" placeholder={t('form.volume')} suffix="m³" />
          </Form.Item>
        </Col>
        {order && (
          <Col span={6}>
            <Form.Item
              name="status"
              label={t('form.status')}
              rules={[{ required: true, message: t('common:required') }]}
            >
              <Select
                options={Object.keys(ORDER_STATUSES_DISPLAY).map((key) => ({
                  value: key,
                  label: ORDER_STATUSES_DISPLAY[key],
                }))}
                placeholder={t('form.orderStatus')}
              />
            </Form.Item>
          </Col>
        )}
        <Col span={4}>
          <Form.Item name="sizeLength" label={t('form.size')}>
            <Input min={1} type="number" suffix="cm" />
          </Form.Item>
        </Col>
        <Col span={4}>
          <Form.Item name="sizeWidth" label=" ">
            <Input min={1} type="number" suffix="cm" />
          </Form.Item>
        </Col>
        <Col span={4}>
          <Form.Item name="sizeHeight" label=" ">
            <Input min={1} type="number" suffix="cm" />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item name="weight" label={t('form.weight')}>
            <Input min={1} type="number" suffix="kg" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label={t('form.supportNote')} name="supportNote">
            <Input.TextArea placeholder={t('form.supportNote')} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="description" label={t('form.description')}>
            <Input.TextArea placeholder={t('form.description')} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label={t('form.issueType')} name="issueType">
            <Select
              placeholder={t('form.issueType')}
              options={Object.keys(ISSUE_TYPES).map((key) => ({ value: key, label: ISSUE_TYPES[key] }))}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label={t('form.partner')} name="partnerId">
            <Select
              placeholder={t('form.selectPartner')}
              showSearch
              allowClear
              optionFilterProp="label"
              options={partners.map((p) => ({ label: p.name, value: p.id }))}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label={t('form.type')} name="types">
            <TagInput size="normal" tags={types} onChange={(values) => setTypes(values)} />
          </Form.Item>
        </Col>
      </Row>

      {/* Order Products Section — only when partner has PRODUCT item types */}
      {selectedPartnerId && productTypes.length > 0 && (
        <>
          <Divider orientation="left">{t('form.products')}</Divider>
          <Row gutter={24}>
            <Col span={24}>
              <Form.Item name="productDetailsKnown" valuePropName="checked" style={{ marginBottom: 12 }}>
                <Checkbox>{t('form.productDetailsKnown')}</Checkbox>
              </Form.Item>
            </Col>
          </Row>
          {productDetailsKnown && (
            <Form.List name="orderProducts">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Row key={key} gutter={12} align="middle" style={{ marginBottom: 8 }}>
                      <Col span={10}>
                        <Form.Item
                          {...restField}
                          name={[name, 'partnerItemTypeId']}
                          rules={[{ required: true, message: t('form.selectProduct') }]}
                          style={{ marginBottom: 0 }}
                        >
                          <Select
                            placeholder={t('form.selectProduct')}
                            options={productTypes.map((t) => ({
                              label: `${t.name}${t.unitOfMeasure ? ` (${t.unitOfMeasure})` : ''}`,
                              value: t.id,
                            }))}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item
                          {...restField}
                          name={[name, 'quantity']}
                          rules={[{ required: true, message: t('form.quantity') }]}
                          style={{ marginBottom: 0 }}
                        >
                          <InputNumber
                            min={0.01}
                            step={0.1}
                            placeholder={t('form.quantity')}
                            style={{ width: '100%' }}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item {...restField} name={[name, 'notes']} style={{ marginBottom: 0 }}>
                          <Input placeholder={t('form.notesOptional')} />
                        </Form.Item>
                      </Col>
                      <Col span={2}>
                        <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(name)} />
                      </Col>
                    </Row>
                  ))}
                  <Button
                    type="dashed"
                    onClick={() => add({ quantity: 1 })}
                    icon={<PlusOutlined />}
                    style={{ width: '50%' }}
                  >
                    {t('form.addProduct')}
                  </Button>
                </>
              )}
            </Form.List>
          )}
          <Divider />
        </>
      )}

      <Row gutter={24}>
        <Col span={6}>
          <Form.Item label={t('form.partnerContact')} name="partnerContact">
            <Input placeholder={t('form.partnerContact')} />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item label={t('form.partnerOrderRef')} name="partnerOrderRef">
            <Input placeholder={t('form.partnerOrderRef')} />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item label={t('form.tourRef')} name="tourRef">
            <Input placeholder={t('form.tourRef')} />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item label={t('form.airtableOrderRecordRef')} name="airtableOrderRecordRef">
            <Input placeholder={t('form.airtableOrderRecordRef')} />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item label={t('form.preparationMode')} name="preparationMode">
            <Input placeholder={t('form.preparationMode')} />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item label={t('form.collectionForm')} name="collectionForm">
            <Input placeholder={t('form.collectionForm')} />
          </Form.Item>
        </Col>
      </Row>
    </div>
  );
};

export default OrderInfoForm;
